/**
 * Pinned tutorial configuration. The set label and baked sketch assets are produced
 * by the bake recipe (plan Task 13) — the label MUST be the exact catalog label
 * (resolveSetSelection does a normalized EXACT match, not fuzzy).
 */
import sketch0 from '/tutorial-sketch-0.jpg?url';
import sketch1 from '/tutorial-sketch-1.jpg?url';
import sketch2 from '/tutorial-sketch-2.jpg?url';

// Finalized by the bake recipe (Task 13): the authored tutorial scene uses NO set — the
// blocked stage (table/chairs/folder stand-ins) IS the look the baked sketches were rendered
// from. 'NONE' is resolveSetSelection's explicit no-set sentinel, so the coverage dialog's
// Set dropdown preselects "None" and Apply dresses nothing, keeping the 3D scene, the live
// thumbnails, and the baked sketch renders visually consistent.
export const TUTORIAL_SET_LABEL = 'NONE';
export const TUTORIAL_RENDER_SHOT_ORDER = 1;
export const FAKE_COVERAGE_OVERLAY_MS = 1800;
export const FAKE_RENDER_SECONDS = 5;

/**
 * 20260921 (design §2.3) — the cast card the CHARACTERS tour opens on, so its panel 3 ("Cast
 * details") has a real expanded card to describe. It is a name from the pre-baked tutorial
 * project's own script, matched CASE-INSENSITIVELY against the roster rather than assumed: the
 * baked asset is regenerated by an Editor tool (design §2.6) and a re-bake that changes the
 * character's case must not silently leave the tour pointing at nothing.
 */
export const CHARACTERS_TOUR_OPEN_CHARACTER = 'DETECTIVE SARAH MILLER';

/**
 * Resolve the cast card the Characters tour should open, as the KEY the Cast list is keyed by —
 * `name.trim().toUpperCase()`, which is what BrowseMode's roster builder (its `rosterByKey`) and
 * `uiStore.screenplayExpandedCharacter` both use. Never the stored, case-preserved name: that one
 * matches no card.
 *
 * Falls back to the first named roster entry, so a re-baked project whose detective was renamed
 * still opens SOMETHING for panel 3 to talk about. Returns null only when there is no roster at
 * all — which is also what a project push that has not landed yet looks like, hence the rail's
 * re-assert (CharactersRail).
 */
export function resolveCharactersTourCastName(
  roster: readonly { name: string }[] | undefined,
): string | null {
  const named = (roster ?? []).filter((c) => c.name.trim() !== '');
  if (named.length === 0) return null;
  const match = named.find(
    (c) => c.name.trim().toUpperCase() === CHARACTERS_TOUR_OPEN_CHARACTER.toUpperCase(),
  );
  return (match ?? named[0]).name.trim().toUpperCase();
}

const SKETCHES: Record<number, string> = { 0: sketch0, 1: sketch1, 2: sketch2 };

export function tutorialSketchUrlForOrder(order: number): string | null {
  return SKETCHES[order] ?? null;
}

/** Shots of the tutorial scene in board order (the baked assets are keyed by this order). */
export function sortedTutorialShots<T extends { id: number; order: number }>(scene: { shots: T[] }): T[] {
  return [...scene.shots].sort((a, b) => a.order - b.order);
}

/** Fetch a baked sketch and convert to a data URL (sendStaticImageToUnity requires data:/blob:). */
export async function loadTutorialSketchDataUrl(order: number): Promise<string | null> {
  const url = tutorialSketchUrlForOrder(order);
  if (!url) return null;
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
